import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import styled from '@mui/material/styles/styled';
import Toolbar from '@mui/material/Toolbar';
import Tooltip, { TooltipProps } from '@mui/material/Tooltip/Tooltip';
import hjs from 'highlight.js';
import cpp from 'highlight.js/lib/languages/cpp';
import csharp from 'highlight.js/lib/languages/csharp';
import vbscript from 'highlight.js/lib/languages/vbscript';
import 'highlight.js/styles/github-dark.css';
import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { KeywordsTranslator } from '../model/Translator';
import { useStyles } from '../styles';

interface ContentProps {
    language: string;
    versions: string[];
    defaultItem: string;
    translated: boolean;
}

interface ThemedContentProps {
    language: string;
    toolHeight: number;
}

const ThemedContent = styled('div')<ThemedContentProps>(({ theme, language, toolHeight }) => ({
    // 通用样式
    fontFamily: theme.typography.fontFamily,
    color: language ? theme.palette.text.primary : theme.palette.text.secondary,
    // header样式
    '& header': {
        position: 'sticky',
        top: 0,
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(0),
        boxShadow: theme.shadows[1],
        zIndex: theme.zIndex.appBar,
    },

    // 导航样式
    '& header nav': {
        '& ul': {
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(3),
            listStyle: 'none',
            padding: 0,
            margin: 0,
        },
        '& li': {
            margin: 0,
            padding: 0,
        },
    },

    // 标题
    '& header h1': {
        ...theme.typography.h4,
        marginTop: theme.spacing(0),
        paddingTop: theme.spacing(2),
    },

    // 段落
    '& p': {
        ...theme.typography.body1,
        marginBottom: theme.spacing(2),
    },

    // 链接
    '& a': {
        color: theme.palette.primary.main,
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },

    // 列表
    '& ul, & li': {
        paddingLeft: theme.spacing(3),
        marginBottom: theme.spacing(2),
    },

    // 表格
    '& table': {
        borderCollapse: 'collapse',
        width: '100%',
        marginBottom: theme.spacing(2),
        '& th, & td': {
            border: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(1.5), // 增加内边距
            textAlign: 'left',
        },
        '& thead th': {
            backgroundColor: theme.palette.primary.main, // 表头背景色
            color: theme.palette.text.primary,
            fontWeight: 'bold',
        },
        '& tbody tr:hover': {
            backgroundColor: theme.palette.action.hover, // 悬停效果
        },
    },

    // 引用
    '& blockquote': {
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        margin: 0,
        padding: theme.spacing(1, 2),
        backgroundColor: theme.palette.action.hover,
    },
    // 处理代码块显示隐藏
    '& span[codelanguage]': {
        display: 'none',
        opacity: 0,
    },
    [`& span[codelanguage="${language}"]`]: {
        display: 'inline',
        opacity: 1,
    },
    maxHeight: `calc(100vh - ${toolHeight}px)`, // Adjust based on your layout
    overflow: 'auto',
}));

const ThemedTooltip = styled(({ className, ...props }: TooltipProps & { className?: string }) => (
    <Tooltip {...props} slotProps={{ popper: { className } }} />
))(({ theme }) => ({
    '& .MuiTooltip-tooltip': {
        fontSize: theme.typography.body1.fontSize,
    },
}));

const ThemedSpan = styled('span')(({ theme }) => ({
    borderBottom: `1px dashed ${theme.palette.primary.main}`,
    '& *': { // 为所有子元素添加虚线下边框
        borderBottom: `1px dashed ${theme.palette.primary.main}`,
    },
}));
const Content: React.FC<ContentProps> = ({ language, versions, defaultItem, translated }) => {
    const [selectedContent, setSelectedContent] = useState('');
    const [loading, setLoading] = useState<boolean>(false);
    const classes = useStyles();
    const toolRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const pathname = useLocation().pathname;
    const navigate = useNavigate();

    useEffect(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const apiVersion = pathname.split('/')[1];
        let html = pathname.split('/').pop();
        if (!html?.endsWith('.htm')) {
            html = defaultItem;
        }
        if (versions.includes(apiVersion)) {
            setLoading(true);
            fetch(`/html/${apiVersion}/${html}`, {
                signal: controller.signal
            })
                .then(response => response.text())
                .then(htmlData => {
                    setSelectedContent(htmlData);
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                })
        }
        return () => {
            controller.abort();
        };
    }, [pathname, versions, defaultItem]);

    useEffect(() => {
        hjs.registerLanguage('vbscript', vbscript);
        hjs.registerLanguage('cpp', cpp);
        hjs.registerLanguage('csharp', csharp);
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }
        translateContent(container, translated); 
        hjs.highlightAll();
        if (translated) {
            const elementsWithTooltip = container.querySelectorAll('[title]');
            elementsWithTooltip.forEach(element => {
                const title = element.getAttribute('title');
                if (title && title.trim().length > 0) {
                    element.removeAttribute('title');
        
                    const tooltip = document.createElement('div');
                    tooltip.className = 'mui-tooltip-container';
        
                    const root = createRoot(tooltip);
                    root.render(
                        <ThemedTooltip title={title}>
                            <ThemedSpan
                                dangerouslySetInnerHTML={{ __html: element.innerHTML }}
                            />
                        </ThemedTooltip>
                    );
        
                    element.innerHTML = '';
                    element.appendChild(tooltip);
                }
            });
        } else {
            const tooltips = container.querySelectorAll('.mui-tooltip-container');
            tooltips.forEach(tooltip => {
                const parentDiv = tooltip.parentElement;
                const span = tooltip.querySelector('span');
                if (parentDiv && span) {
                    // 恢复子节点结构
                    parentDiv.innerHTML = span.innerHTML;
                    const title = span.getAttribute('aria-label');
                    parentDiv.setAttribute('title', title || '');
                }
            });
        }

    }, [selectedContent, translated]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLAnchorElement;

            // 判断是否点击了 <a> 标签
            if (target.tagName === "A" && target.href) {
                const url = new URL(target.href, window.location.origin);

                if (url.hash) {
                    return;
                }

                // 判断是否是相对当前网站的链接
                if (url.origin === window.location.origin) {
                    event.preventDefault();
                    navigate(url.pathname);
                }
            }
        };

        // 绑定事件监听器
        container?.addEventListener("click", handleClick);

        // 清理事件监听器
        return () => {
            container?.removeEventListener("click", handleClick);
        };
    }, [navigate, selectedContent]);

    if (!selectedContent) {
        return null;
    }
    const { title, metaTags } = getTitleMeta(selectedContent);
    return (
        <Box component="main" sx={classes.content}>
            <Toolbar ref={toolRef} />
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', height: '200px', alignItems: 'center', flexGrow: 1 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <ThemedContent
                        id="dynamic-content"
                        ref={containerRef}
                        dangerouslySetInnerHTML={{ __html: selectedContent }}
                        language={language}
                        toolHeight={toolRef.current?.offsetHeight || 80}
                    />

                    <Helmet>
                        {title.length && <title>{translated? KeywordsTranslator.translateTitle(title): title}</title>}

                        {metaTags.map((meta, index) => (
                            <meta key={index} name={meta.name} content={meta.content} />
                        ))}
                    </Helmet>
                </Box>
            )}
        </Box>
    );
};

interface TitleMeta {
    title: string;
    metaTags: Array<{ name: string; content: string }>;
}
function getTitleMeta(htmlText: string): TitleMeta {
    const titleMeta: TitleMeta = { title: '', metaTags: [] };
    if (htmlText.trim() === '') {
        return titleMeta;
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlText, "text/html");

    const titleElement = doc.querySelector("title");
    if (titleElement) {
        titleMeta.title = (titleElement.textContent || "");
    }

    const metaElements = Array.from(doc.querySelectorAll("meta"));
    titleMeta.metaTags = metaElements
        .map((meta) => ({
            name: meta.getAttribute("name") || '',
            content: meta.getAttribute("content") || '',
        }))
        .filter((meta) => {
            return !["file", "guid", "Language", "Microsoft.Help.Id", "Microsoft.Help.ContentType", "save"].includes(meta.name);
        })
        .map((meta) => {
            if (meta.name === "Microsoft.Help.F1") {
                return { name: "Description", content: meta.content };
            }
            if (meta.name === "container") {
                return { name: "Namespace", content: meta.content };
            }
            if (meta.name === "System.Keywords") {
                return { name: "Keywords", content: meta.content };
            }

            return meta;
        });
    return titleMeta;
}

function translate(element: Element, translated: boolean, isTitle = false) {
    const originalText = 'original-text';    
    if (translated) {
        const text = element.textContent?.trim() || '';
        element.setAttribute(originalText, text);
        element.textContent = isTitle? KeywordsTranslator.translateTitle(text) : KeywordsTranslator.translate(text);
    } else {
        if (element.hasAttribute(originalText)) {
            element.textContent = element.getAttribute(originalText);
            element.removeAttribute(originalText);
        }
    }
}
function translateSpan(element: Element, translated: boolean) {
    const originalText = 'original-text';    
    if (translated) {
        const childNodes = element.childNodes;
        if (childNodes.length > 0) {
            const lastChild = childNodes[childNodes.length - 1];
            if (lastChild.nodeType === Node.TEXT_NODE) {
                const text = lastChild.textContent?.trim() || '';
                lastChild.textContent = KeywordsTranslator.translateTitle(text);
                element.setAttribute(originalText, text);
            }
        }        

    } else {
        if (element.hasAttribute(originalText)) {
            const text = element.getAttribute(originalText);
            const childNodes = element.childNodes;
            if (childNodes.length > 0) {
                const lastChild = childNodes[childNodes.length - 1];
                if (lastChild.nodeType === Node.TEXT_NODE) {
                    lastChild.textContent = text;
                }
            }   
        }
    }
}
const translateContent = (content: HTMLElement, translated: boolean) => {
    // 翻译 h1
    content.querySelectorAll('h1 > span').forEach(element => {
        const span = element.querySelector('span');
        if (span) {
            translateSpan(element, translated);
        } else {
            translate(element, translated, true);
        }
    });
    // 翻译 h2, h4
    content.querySelectorAll('h2, h4').forEach(element => {
        translate(element, translated);
    });

    // 翻译 table 中的 th
    content.querySelectorAll('table th').forEach(element => {
        translate(element, translated);
    });

    // 翻译 <b>
    content.querySelectorAll('b').forEach(element => {
        translate(element, translated);
    });

    // 翻译 header > nav > li 中的 <a>
    content.querySelectorAll('header > nav > ul > li').forEach((li,index) => {
        const a = li.querySelector('a');
        if (a) {
            translate(a, translated, index === 0);
        } else {
            const lastChild = li.lastElementChild;
            if (lastChild) {
                translate(lastChild, translated, index === 0);
            }
        }
    });
};


export default Content;


